import { Card, Typography, Space, Badge } from "antd";
import { CSSProperties, ReactNode } from "react";
import { colors, shadows } from "config/theme";

const { Title, Text } = Typography;

interface ShowSectionProps {
  title?: string;
  subtitle?: string;
  icon?: ReactNode;
  badge?: string;
  badgeColor?: string;
  extra?: ReactNode;
  children: ReactNode;
  variant?: "default" | "elevated" | "outlined" | "subtle";
  padding?: "default" | "compact" | "spacious";
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

const variantStyles: Record<string, CSSProperties> = {
  default: {
    background: colors.bgBase,
    border: `1px solid ${colors.borderLight}`,
    boxShadow: "none",
  },
  elevated: {
    background: colors.bgBase,
    border: "none",
    boxShadow: shadows.sm,
  },
  outlined: {
    background: "transparent",
    border: `1px solid ${colors.borderDefault}`,
    boxShadow: "none",
  },
  subtle: {
    background: colors.bgSubtle,
    border: `1px solid ${colors.borderLight}`,
    boxShadow: "none",
  },
};

const paddingStyles: Record<string, { padding: number }> = {
  compact: { padding: 16 },
  default: { padding: 24 },
  spacious: { padding: 32 },
};

const sectionStyles = {
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 20,
    paddingBottom: 16,
    borderBottom: `1px solid ${colors.borderLight}`,
  } as CSSProperties,
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  } as CSSProperties,
  iconWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: colors.containerBg,
    color: colors.siderBg,
    fontSize: 16,
  } as CSSProperties,
  titleGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  } as CSSProperties,
  title: {
    fontSize: 16,
    fontWeight: 600,
    color: colors.textPrimary,
    margin: 0,
    lineHeight: 1.4,
  } as CSSProperties,
  subtitle: {
    fontSize: 13,
    color: colors.textTertiary,
    margin: 0,
    lineHeight: 1.4,
  } as CSSProperties,
};

export const ShowSection: React.FC<ShowSectionProps> = ({
  title,
  subtitle,
  icon,
  badge,
  badgeColor = "default",
  extra,
  children,
  variant = "default",
  padding = "default",
}) => {
  const hasHeader = title || subtitle || icon || badge || extra;

  return (
    <Card
      style={{
        borderRadius: 12,
        ...variantStyles[variant],
      }}
      styles={{
        body: {
          ...paddingStyles[padding],
        },
      }}
    >
      {hasHeader && (
        <div style={sectionStyles.header}>
          <div style={sectionStyles.headerLeft}>
            {icon && <div style={sectionStyles.iconWrapper}>{icon}</div>}
            <div style={sectionStyles.titleGroup as CSSProperties}>
              <Space size={8} align="center">
                {title && (
                  <Title level={5} style={sectionStyles.title}>
                    {title}
                  </Title>
                )}
                {badge && (
                  <Badge
                    count={badge}
                    color={badgeColor}
                    style={{ fontWeight: 500 }}
                  />
                )}
              </Space>
              {subtitle && (
                <Text style={sectionStyles.subtitle}>{subtitle}</Text>
              )}
            </div>
          </div>
          {extra && <div>{extra}</div>}
        </div>
      )}
      {children}
    </Card>
  );
};

export default ShowSection;
